import { Title, Description } from '@radix-ui/react-dialog';

import { OverlayCommonProps } from './types';

import { StringNodeGuard } from '../helpers';
import { cn } from 'styles';

type HeaderProps = Pick<OverlayCommonProps, 'title' | 'description'> & {
  /**
   * Switches the proper components depending of the parent overlay type
   *
   * Example: popover can't use DialogTitle inside of it...
   */
  isDialog?: boolean;

  /**
   * Controls the text size of the default text references
   *
   * Reduced basically means 1 less for each (title and description)
   *
   * Example:
   * - title: lg => base
   * - description sm -> xs
   */
  sizing?: 'normal' | 'reduced';
};

export function OverlayTitle({
  isDialog,
  sizing,
  title,
}: Pick<HeaderProps, 'title' | 'sizing' | 'isDialog'>) {
  const ActualTitle = isDialog ? Title : 'h2';

  return (
    <StringNodeGuard node={title}>
      <ActualTitle
        className={cn(
          'font-semibold leading-none tracking-tight',
          sizing === 'reduced' ? 'text-base' : 'text-lg',
        )}
      >
        {title}
      </ActualTitle>
    </StringNodeGuard>
  );
}

export function OverlayHeaderDescription({
  isDialog,
  sizing,
  description,
}: Pick<HeaderProps, 'description' | 'sizing' | 'isDialog'>) {
  const ActualDescription = isDialog ? Description : 'p';

  return (
    <StringNodeGuard node={description}>
      <ActualDescription
        className={cn(
          'text-sm text-muted-foreground',
          sizing === 'reduced' ? 'text-xs' : 'text-sm',
        )}
      >
        {description}
      </ActualDescription>
    </StringNodeGuard>
  );
}

export function OverlayHeader({ isDialog = true, ...props }: HeaderProps) {
  const ActualTitle = isDialog ? Title : 'h2';

  if (!props.description && !props.title) return <ActualTitle className="hidden">.</ActualTitle>;

  const isReduced = props.sizing === 'reduced';

  return (
    <div className={cn('flex flex-col', isReduced ? 'gap-0.5' : 'gap-1.5')}>
      <OverlayTitle isDialog={isDialog} {...props} />

      {/* Prevent warning */}
      <ActualTitle className="hidden">.</ActualTitle>

      <OverlayHeaderDescription isDialog={isDialog} {...props} />
    </div>
  );
}
